import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Phone, PhoneCall, CircleNotch, CheckCircle, XCircle, Bug } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";

const STATUS_STYLE = {
  pending: "bg-[#E5E7EB] text-[#374151]",
  initiated: "bg-[#DBEAFE] text-[#1E40AF]",
  ringing: "bg-[#FEF3C7] text-[#92400E]",
  in_progress: "bg-[#DBEAFE] text-[#1E40AF]",
  completed: "bg-[#D1FAE5] text-[#065F46]",
  failed: "bg-[#FEE2E2] text-[#991B1B]",
};

const DTMF_LABEL = {
  "1": { label: "Confirmed", color: "bg-[#D1FAE5] text-[#065F46]" },
  "2": { label: "Declined", color: "bg-[#FEE2E2] text-[#991B1B]" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return formatDistanceToNow(d, { addSuffix: true });
  } catch (_) {
    return "—";
  }
}

export default function CallDriverDialog({ open, onOpenChange, contact }) {
  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [debugFor, setDebugFor] = useState(null); // call_id whose debug is open
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  const loadDebug = async (callId) => {
    if (debugFor === callId) {
      // toggle close
      setDebugFor(null);
      setDebugData(null);
      return;
    }
    setDebugFor(callId);
    setDebugLoading(true);
    setDebugData(null);
    try {
      const r = await api.get(`/voice/calls/${callId}`);
      setDebugData(r.data);
    } catch (_) {
      toast.error("Failed to load debug info");
    } finally {
      setDebugLoading(false);
    }
  };

  const copyDebug = () => {
    if (!debugData) return;
    navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
    toast.success("Debug JSON copied");
  };

  const driverName = contact?.name || "";
  const driverPhone = contact?.phone || "";

  // Load recent calls for this driver
  const loadHistory = async () => {
    if (!driverPhone) return;
    setLoadingHistory(true);
    try {
      const r = await api.get("/voice/calls", { params: { limit: 50 } });
      const items = (r.data.items || []).filter((c) => c.driver_phone === driverPhone);
      setHistory(items);
    } catch (_) {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPickup("");
      setDrop("");
      setTime("");
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, driverPhone]);

  // Poll history when there's an in-flight call
  useEffect(() => {
    if (!open) return;
    const inFlight = history.some(
      (c) => c.status === "initiated" || c.status === "ringing" || c.status === "in_progress",
    );
    if (!inFlight) return;
    const t = setInterval(loadHistory, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!driverPhone) return toast.error("No driver selected");
    if (!pickup.trim() || !drop.trim() || !time.trim()) {
      return toast.error("Pickup, drop aur time tino bhariye");
    }
    setSubmitting(true);
    try {
      const r = await api.post("/voice/calls", {
        driver_phone: driverPhone,
        driver_name: driverName,
        pickup: pickup.trim(),
        drop: drop.trim(),
        time: time.trim(),
      });
      toast.success(`Call initiated — ${r.data.message || "Tata is dialing driver"}`);
      setPickup(""); setDrop(""); setTime("");
      loadHistory();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Call failed to initiate");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--surface)] border-[var(--border)] text-[var(--fg)] max-w-2xl"
        data-testid="call-driver-dialog"
      >
        <DialogHeader>
          <DialogTitle className="heading text-xl flex items-center gap-2">
            <PhoneCall size={22} className="text-[#25D366]" weight="fill" /> Call Driver
          </DialogTitle>
          <DialogDescription className="text-xs text-[var(--muted)]">
            Tata Smartflo + AI voice (Hindi). Driver ko ring jayegi aur ride details TTS me sunaai jayegi.
            Driver press kar sakta hai: <b>1</b> = Confirm, <b>2</b> = Decline.
          </DialogDescription>
        </DialogHeader>

        {/* Driver summary */}
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg)] p-3 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[var(--fg)] text-[var(--bg)] flex items-center justify-center font-bold">
            <Phone size={16} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate" data-testid="call-driver-name">
              {driverName || "(no name)"}
            </div>
            <div className="text-xs font-mono text-[var(--muted)]" data-testid="call-driver-phone">
              +{driverPhone}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="call-driver-form">
          <div className="space-y-1">
            <Label htmlFor="cd-pickup" className="text-xs">Pickup location *</Label>
            <Input
              id="cd-pickup"
              value={pickup}
              onChange={(e) => setPickup(e.target.value)}
              placeholder="e.g. Patna Junction"
              className="bg-[var(--bg)] border-[var(--border)]"
              data-testid="call-driver-pickup"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cd-drop" className="text-xs">Drop location *</Label>
            <Input
              id="cd-drop"
              value={drop}
              onChange={(e) => setDrop(e.target.value)}
              placeholder="e.g. Darbhanga"
              className="bg-[var(--bg)] border-[var(--border)]"
              data-testid="call-driver-drop"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cd-time" className="text-xs">Pickup time *</Label>
            <Input
              id="cd-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g. raat 10 baje"
              className="bg-[var(--bg)] border-[var(--border)]"
              data-testid="call-driver-time"
              required
            />
          </div>

          <div className="md:col-span-3 flex items-center justify-between gap-3 pt-1">
            <p className="text-[11px] text-[var(--muted)] leading-relaxed">
              Driver sunega: <i>"नमस्ते {driverName || "{name}"}, आपको <b>{pickup || "{pickup}"}</b> से <b>{drop || "{drop}"}</b> के लिए <b>{time || "{time}"}</b> का राइड असाइन किया गया है…"</i>
            </p>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5 shrink-0"
              data-testid="call-driver-submit"
            >
              {submitting ? (
                <><CircleNotch size={14} className="animate-spin" /> Dialing…</>
              ) : (
                <><PhoneCall size={14} weight="fill" /> Call Now</>
              )}
            </Button>
          </div>
        </form>

        {/* Recent calls for this driver */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide">
              Recent calls to this driver
            </div>
            {loadingHistory && <CircleNotch size={12} className="animate-spin text-[var(--muted)]" />}
          </div>
          {history.length === 0 ? (
            <div className="text-[11px] text-[var(--muted)] text-center py-4 border border-dashed border-[var(--border)] rounded-md">
              No calls yet. Initiate one above.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1" data-testid="call-driver-history">
              {history.map((c) => (
                <div key={c.id} data-testid={`call-history-row-${c.id}`}>
                <div
                  className="flex items-center gap-3 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      <b>{c.pickup || "—"}</b> → <b>{c.drop || "—"}</b>
                      <span className="text-[var(--muted)]"> · {c.time || "—"}</span>
                    </div>
                    <div className="text-[10px] text-[var(--muted)]">
                      {timeAgo(c.created_at)}
                      {c.duration_seconds ? ` · ${c.duration_seconds}s` : ""}
                      {c.error ? ` · ${c.error.slice(0, 60)}` : ""}
                    </div>
                  </div>
                  <Badge className={`${STATUS_STYLE[c.status] || STATUS_STYLE.pending} text-[10px] uppercase tracking-wide px-1.5 py-0.5`}>
                    {c.status}
                  </Badge>
                  {c.dtmf_response && DTMF_LABEL[c.dtmf_response] && (
                    <Badge className={`${DTMF_LABEL[c.dtmf_response].color} text-[10px] px-1.5 py-0.5 gap-0.5`}>
                      {c.dtmf_response === "1" ? <CheckCircle size={10} weight="fill" /> : <XCircle size={10} weight="fill" />}
                      {DTMF_LABEL[c.dtmf_response].label}
                    </Badge>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 shrink-0"
                    onClick={() => loadDebug(c.id)}
                    title="Show debug / WSS frame log"
                    data-testid={`call-debug-btn-${c.id}`}
                  >
                    <Bug size={14} />
                  </Button>
                </div>
                {debugFor === c.id && (
                  <div className="mt-1 rounded-md border border-[#FCD34D] bg-[#FFFBEB] p-3 text-[11px] space-y-2" data-testid={`call-debug-panel-${c.id}`}>
                    {debugLoading && <div className="flex items-center gap-2 text-[var(--muted)]"><CircleNotch size={12} className="animate-spin" /> Loading…</div>}
                    {debugData && (
                      <>
                        <div className="flex items-center justify-between">
                          <b className="text-[#92400E]">Debug info</b>
                          <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={copyDebug} data-testid="copy-debug-btn">
                            Copy JSON
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                          <div><b>Tata ref_id:</b> <code className="font-mono">{debugData.tata_ref_id || "—"}</code></div>
                          <div><b>TTS duration:</b> {debugData.tts_duration_seconds ? `${debugData.tts_duration_seconds}s` : "—"}</div>
                          <div><b>TTS error:</b> {debugData.tts_error || "—"}</div>
                          <div><b>Frames received:</b> {(debugData.wss_frame_log || []).length}</div>
                        </div>
                        <div>
                          <b className="text-[10px] uppercase tracking-wide">WSS frame log ({(debugData.wss_frame_log || []).length})</b>
                          {(debugData.wss_frame_log || []).length === 0 ? (
                            <div className="text-[var(--muted)] italic mt-1">
                              ⚠ No frames received from Tata. Means either Tata WSS never connected (production not redeployed yet?) or Tata closed before sending anything.
                            </div>
                          ) : (
                            <pre className="bg-[#1F2937] text-[#F3F4F6] rounded p-2 overflow-auto max-h-[200px] text-[10px] mt-1 font-mono">
{JSON.stringify(debugData.wss_frame_log, null, 2)}
                            </pre>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
